import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useTranslation } from "react-i18next";
import { getInitials } from "../../../utils/utils";
import { buildTeamMessageReceiptLists } from "../utils/teamMessageReceiptUtils";
import { formatMessageDateTime, TEAM_MOBILE_MEDIA_QUERY } from "../utils/teamUiUtils";

function ReceiptList({ items, emptyLabel }) {
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, px: 2, textAlign: "center" }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <List dense disablePadding>
      {items.map((item) => (
        <ListItem key={item.userId} divider>
          <ListItemAvatar>
            <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main", fontSize: "0.875rem" }}>
              {getInitials(item.displayName)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={item.displayName}
            secondary={item.at ? formatMessageDateTime(item.at) : null}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItem>
      ))}
    </List>
  );
}

const TeamMessageReceiptDialog = ({ open, onClose, message, spaceType }) => {
  const { t } = useTranslation("team");
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);

  const lists = useMemo(
    () =>
      buildTeamMessageReceiptLists({
        receipts: message?.receipts,
        messageType: message?.messageType,
      }),
    [message?.receipts, message?.messageType]
  );

  const tabs = useMemo(() => {
    const result = [];
    if (lists.isAudio) {
      result.push({ key: "played", label: t("receiptTabPlayed"), count: lists.played.length });
    }
    result.push({ key: "read", label: t("receiptTabRead"), count: lists.read.length });
    result.push({ key: "delivered", label: t("receiptTabDelivered"), count: lists.delivered.length });
    if (lists.pending.length > 0) {
      result.push({ key: "pending", label: t("receiptTabPending"), count: lists.pending.length });
    }
    return result;
  }, [lists, t]);

  const [activeTab, setActiveTab] = useState(0);

  const currentTabKey = tabs[activeTab]?.key || "read";
  const currentItems = lists[currentTabKey] || [];

  const emptyLabels = {
    read: t("receiptEmptyRead"),
    played: t("receiptEmptyPlayed"),
    delivered: t("receiptEmptyDelivered"),
    pending: t("receiptEmptyPending"),
  };

  const isDm = spaceType === "dm";
  const peer = isDm ? lists.read[0] || lists.played[0] || lists.delivered[0] || lists.pending[0] : null;

  const dmStatusLabel = (() => {
    if (!peer) return t("receiptDmPending");
    if (lists.played.length) return t("receiptDmPlayed", { at: formatMessageDateTime(lists.played[0].at) });
    if (lists.read.length) return t("receiptDmRead", { at: formatMessageDateTime(lists.read[0].at) });
    if (lists.delivered.length) return t("receiptDmDelivered", { at: formatMessageDateTime(lists.delivered[0].at) });
    return t("receiptDmPending");
  })();

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: isDm ? 1 : undefined }}>
        <DoneAllIcon fontSize="small" color="info" />
        {t("receiptDialogTitle")}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isDm ? (
          <Box sx={{ px: 2, py: 2 }}>
            {peer ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
                  {getInitials(peer.displayName)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">{peer.displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dmStatusLabel}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("receiptDmPending")}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.key}
                  label={
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      <span>{tab.label}</span>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {tab.count}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Tabs>
            <ReceiptList items={currentItems} emptyLabel={emptyLabels[currentTabKey]} />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t("receiptDialogClose")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMessageReceiptDialog;
